import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useBlocker } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import { FaEdit, FaTrash, FaClone } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import WalterLoader from '../shared/WalterLoader';
import Swal from 'sweetalert2';
import L from 'leaflet';
import 'react-toastify/dist/ReactToastify.css';
import 'leaflet/dist/leaflet.css';
import '../../styles/Audit.css';

import BuildingUsersReadOnly from './auditSubsections/BuildingUsersReadOnly';
import FunctionalUnitsForm from './auditSubsections/FunctionalUnitsForm';
import CbsPanel from './auditSubsections/CbsPanel';
import { useAppMetadata } from '../shared/useAppMetadata';
import metadata from '../../metadata.json';

const HOT_WATER_PRODUCTION = metadata.hot_water_production;
// Champ facultatif : vide → null (le calcul prend alors la valeur par défaut).
const numberOrNull = (value) => (value === '' || value == null || Number.isNaN(parseFloat(value)) ? null : parseFloat(value));


delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const defaultForm = {
  buildingName: '',
  buildingSurface: '',
  numberOfFloors: '',
  plotSurface: '',
  personCount: '',
  buildingType: '',
  latitude: '',
  longitude: '',
  adress: '',
  city: '',
  watershed: 'adour_garonne',
  postalCode: '',
  auditDate: '',
  auditorName1: 'Matthieu Annequin',
  auditorMail1: 'matthieu.annequin@hydros-consulting.com',
  auditorName2: 'Valentin Odde',
  auditorMail2: 'valentin.odde@hydros-consulting.com',
  respondent: '',
  respondentMail: '',
  buildingTypeRoof: '',
  isGreenRoof: false,
  surfaceRoof: '',
  cbs: '',
  daysPerWeek: '5',
  weeksPerYear: '52',
  moreIntenseWeeks: '0',
  lessIntenseWeeks: '0',
  counterBrand: '',
  counterSize: '',
  counterIndex:'',
  waterSupplier: '',
  billingFrequency: '', 
  remoteReading: false, 
  submeter : false,
  waterConsumptionYear1 : '',
  waterConsumptionYear2 : '',
  waterConsumptionYear3 : '',
  waterConsumptionPrice1 : '',
  waterConsumptionPrice2 : '',
  waterConsumptionPrice3 : '',
  referenceConsumption : '',
  referenceConsumptionComment : '',
  waterHeatingType : 'electricity', 
  waterPrice : '', 
  electricityPrice : 0.2016, 
  fuelPrice : 0.1346,
  hotWaterSetpoint : '',
  waterHeatingEfficiency : '',
  building_users: [],
  functionalUnits: []
}

function UpdateMapView({ coords }) {
  const map = useMap();
  useEffect(() => {
    if (coords) {
      map.setView(coords, 13);
    }
  }, [coords, map]);
  return null;
}

function AuditPageCreationBuilding() {
  const navigate = useNavigate();
  const { id_client } = useParams();
  
  const [formData, setFormData] = useState(defaultForm);

  const [buildings, setBuildings] = useState([]);
  const [dataClient, setDataClient] = useState(null);
  const [coordinatesFound, setCoordinatesFound] = useState(false);
  const [verifyButtonText, setVerifyButtonText] = useState("Vérifier l'adresse");
  const [verifyButtonClass, setVerifyButtonClass] = useState("");
  const [addressVerified, setAddressVerified] = useState(false);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingBuildingId, setEditingBuildingId] = useState(null);
  const [activeEditButton, setActiveEditButton] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [baselineFormData, setBaselineFormData] = useState(defaultForm);
  const skipBlockRef = useRef(false);
  const { data: buildingTypes } = useAppMetadata('building_types');

  const isDirty = JSON.stringify(formData) !== JSON.stringify(baselineFormData);

  // Le calcul du CBS est déjà enregistré par l'API : on aligne le formulaire et sa référence
  // (sans le rendre « modifié ») et la liste des bâtiments, qui porte cbs_detail.
  const handleCbsComputed = (detail) => {
    setFormData((prev) => ({ ...prev, cbs: detail.cbs }));
    setBaselineFormData((prev) => ({ ...prev, cbs: detail.cbs }));
    setBuildings((prev) => prev.map((b) => (b._id === editingBuildingId ? { ...b, cbs: detail.cbs, cbs_detail: detail } : b)));
  };

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && !skipBlockRef.current && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      Swal.fire({
        title: 'Modifications non enregistrées',
        html:
          'Vous avez des modifications non enregistrées sur ce bâtiment.<br>' +
          'Voulez-vous quitter cette page sans enregistrer ?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Quitter sans enregistrer',
        cancelButtonText: 'Rester sur la page',
        customClass: 'swal-custom'
      }).then((result) => {
        if (result.isConfirmed) {
          blocker.proceed();
        } else {
          blocker.reset();
        }
      });
    }
  }, [blocker]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    fetchBuildings();
  }, []);

  useEffect(() => {
    if (addressVerified) {
      if (formData.latitude && formData.longitude) {
        setCoordinatesFound(true);
        setVerifyButtonText("Adresse correcte !");
        setVerifyButtonClass("");
      } else {
        setCoordinatesFound(false);
        setVerifyButtonText("Adresse incorrecte");
        setVerifyButtonClass("incorrect");
      }
    }
  }, [formData.latitude, formData.longitude, addressVerified]);

  const getGpsCoordinates = async (street, postalCode, city) => {
    try {
        const fullAddress = `${street}, ${postalCode} ${city}`;
        const url = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(fullAddress)}`;

        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Erreur HTTP : ${response.status}`);
        }

        const data = await response.json();

        if (data.features.length > 0) {
            const [lng, lat] = data.features[0].geometry.coordinates;
            setFormData(prevState => ({
              ...prevState,
              latitude: lat,
              longitude: lng,
            }));
        } else {
            console.warn("Adresse non trouvée.");
            setFormData(prevState => ({
              ...prevState,
              latitude: '',
              longitude: '',
            }));
        }
    } catch (error) {
        console.error("Erreur lors de la requête :", error);
        setFormData(prevState => ({
          ...prevState,
          latitude: '',
          longitude: '',
        }));
    }
};

  const fetchBuildings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/get_buildings_from_client_id/${id_client}`);
      if (response.ok) {
        const data = await response.json();
        setBuildings(data);
      }
    } catch (error) {
      console.error("Erreur API:", error);
    }
  };


  const handleCreate = async (event) => {
    event.preventDefault();
    setIsSaving(true);

    try {
      // 1️⃣ Construction du bâtiment
      const buildingData = {
        _id: uuidv4(),
        id_client,
        name_building: formData.buildingName,
        type_building: formData.buildingType,
        watershed: formData.watershed,
        surface: parseFloat(formData.buildingSurface),
        plot_surface: parseFloat(formData.plotSurface),
        number_of_floors: formData.numberOfFloors,
        person_count: parseInt(formData.personCount),
        adress: formData.adress,
        city: formData.city,
        postal_code: formData.postalCode,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),

        roof: {
          building_type_roof: formData.buildingTypeRoof,
          is_green_roof: formData.isGreenRoof,
          surface_roof: parseFloat(formData.surfaceRoof),
        },

        cbs: parseFloat(formData.cbs),

        building_usage: {
          weekly_usage: parseFloat(formData.daysPerWeek),
          yearly_usage: parseFloat(formData.weeksPerYear),
          more_intense_weeks: parseFloat(formData.moreIntenseWeeks),
          less_intense_weeks: parseFloat(formData.lessIntenseWeeks),
        },
        building_users : formData.building_users || [],
        functional_units : formData.functionalUnits || [],

        audit: {
          audit_date: formData.auditDate,
          auditor_name_1: formData.auditorName1,
          auditor_name_2: formData.auditorName2,
          auditor_mail_1: formData.auditorMail1,
          auditor_mail_2: formData.auditorMail2,
          respondent: formData.respondent,
          respondent_mail: formData.respondentMail,
        },

        counter: {
          counter_brand: formData.counterBrand,
          counter_size: formData.counterSize,
          counter_index: formData.counterIndex,
          water_supplier: formData.waterSupplier,
          billing_frequency: formData.billingFrequency,
          remote_reading: formData.remoteReading,
          submeter: formData.submeter,
        },

        global_water_data: {
          water_consumption_year_1: parseFloat(formData.waterConsumptionYear1),
          water_consumption_year_2: parseFloat(formData.waterConsumptionYear2),
          water_consumption_year_3: parseFloat(formData.waterConsumptionYear3),
          water_consumption_price_1: parseFloat(formData.waterConsumptionPrice1),
          water_consumption_price_2: parseFloat(formData.waterConsumptionPrice2),
          water_consumption_price_3: parseFloat(formData.waterConsumptionPrice3),
          reference_consumption: numberOrNull(formData.referenceConsumption),
          reference_consumption_comment: formData.referenceConsumptionComment,
          water_heating_type: formData.waterHeatingType,
          water_price: parseFloat(formData.waterPrice),
          electricity_price: parseFloat(formData.electricityPrice),
          fuel_price: parseFloat(formData.fuelPrice),
          hot_water_setpoint: numberOrNull(formData.hotWaterSetpoint),
          water_heating_efficiency: numberOrNull(formData.waterHeatingEfficiency),
        },
      };

      // 2️⃣ Récupération des risques
      console.log("API risques lancée");
      const risksData = await fetchRisksData(formData.latitude, formData.longitude);
      buildingData.risks = risksData.risquesNaturels;

      // 3️⃣ Envoi à l'API
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/load_new_building`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildingData),
        }
      );

      // 4️⃣ Gestion des retours
      if (response.ok) {
        toast.success("Bâtiment enregistré avec succès !");
        clearForm();
        fetchBuildings();
        skipBlockRef.current = true;
        navigate(`/audit/${id_client}/${buildingData._id}`);
      } else {
        toast.error("Erreur lors de l'enregistrement du bâtiment.");
      }

    } catch (error) {
      console.error('Erreur API:', error);
      toast.error("Erreur de connexion à l'API.");
    } finally {
      setIsSaving(false);
    }
  };



  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);

    try {
      const {
        daysPerWeek,
        weeksPerYear,
        moreIntenseWeeks,
        lessIntenseWeeks,
      } = formData;

      const editingBuilding = buildings.find(b => b._id === editingBuildingId);
      if (!editingBuilding) {
        toast.error("Impossible de trouver le bâtiment à modifier.");
        setIsSaving(false);
        return;
      }

      /* =========================
        1️⃣ Détection usages globaux
        ========================= */
      const usageChanged =
        parseFloat(daysPerWeek) !== parseFloat(editingBuilding.building_usage?.weekly_usage) ||
        parseFloat(weeksPerYear) !== parseFloat(editingBuilding.building_usage?.yearly_usage) ||
        parseFloat(moreIntenseWeeks) !== parseFloat(editingBuilding.building_usage?.more_intense_weeks) ||
        parseFloat(lessIntenseWeeks) !== parseFloat(editingBuilding.building_usage?.less_intense_weeks);

      /* =========================
        2️⃣ Pop-up usages globaux
        ========================= */
      if (usageChanged) {
        const result = await Swal.fire({
          title: 'Modification détectée',
          html:
            'Vous avez modifié une ou plusieurs valeurs de présence dans le bâtiment.<br>' +
            'Voulez-vous appliquer ces modifications à tous les équipements et usages du bâtiment ?',
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Oui, appliquer',
          cancelButtonText: 'Non, ne pas appliquer',
        });

        if (result.isConfirmed) {
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/update_usages_by_building`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                building_id: editingBuildingId,
                daysPerWeek: parseFloat(daysPerWeek),
                weeksPerYear: parseFloat(weeksPerYear),
                moreIntenseWeeks: parseFloat(moreIntenseWeeks),
                lessIntenseWeeks: parseFloat(lessIntenseWeeks),
              }),
            });

            if (response.ok) {
              toast.success("Modifications appliquées à tous les équipements et usages !");
            } else {
              toast.error("Erreur lors de l'application des modifications.");
            }
          } catch (error) {
            console.error("Error during API request:", error);
            toast.error("Erreur de connexion à l'API.");
          }
        }
      }

      /* =========================
        3️⃣ Construction du buildingData complet
        ========================= */
      const buildingData = {
        _id: editingBuildingId,
        id_client,
        name_building: formData.buildingName,
        type_building: formData.buildingType,
        watershed: formData.watershed,
        surface: parseFloat(formData.buildingSurface),
        plot_surface: parseFloat(formData.plotSurface),
        number_of_floors: formData.numberOfFloors,
        person_count: parseInt(formData.personCount),
        adress: formData.adress,
        city: formData.city,
        postal_code: formData.postalCode,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        roof: {
          building_type_roof: formData.buildingTypeRoof,
          is_green_roof: formData.isGreenRoof,
          surface_roof: parseFloat(formData.surfaceRoof)
        },
        cbs: parseFloat(formData.cbs),
        building_usage: {
          weekly_usage: parseFloat(formData.daysPerWeek),
          yearly_usage: parseFloat(formData.weeksPerYear),
          more_intense_weeks: parseFloat(formData.moreIntenseWeeks),
          less_intense_weeks: parseFloat(formData.lessIntenseWeeks),
        },
        building_users : formData.building_users || [],
        functional_units : formData.functionalUnits || [],
        audit: {
          audit_date: formData.auditDate,
          auditor_name_1: formData.auditorName1,
          auditor_name_2: formData.auditorName2,
          auditor_mail_1: formData.auditorMail1,
          auditor_mail_2: formData.auditorMail2,
          respondent: formData.respondent,
          respondent_mail: formData.respondentMail
        },
        counter: {
          counter_brand: formData.counterBrand,
          counter_size: formData.counterSize,
          counter_index: formData.counterIndex,
          water_supplier: formData.waterSupplier,
          billing_frequency: formData.billingFrequency,
          remote_reading: formData.remoteReading,
          submeter: formData.submeter
        },
        global_water_data: {
          water_consumption_year_1: parseFloat(formData.waterConsumptionYear1),
          water_consumption_year_2: parseFloat(formData.waterConsumptionYear2),
          water_consumption_year_3: parseFloat(formData.waterConsumptionYear3),
          water_consumption_price_1: parseFloat(formData.waterConsumptionPrice1),
          water_consumption_price_2: parseFloat(formData.waterConsumptionPrice2),
          water_consumption_price_3: parseFloat(formData.waterConsumptionPrice3),
          reference_consumption: numberOrNull(formData.referenceConsumption),
          reference_consumption_comment: formData.referenceConsumptionComment,
          water_heating_type: formData.waterHeatingType,
          water_price: parseFloat(formData.waterPrice),
          electricity_price: parseFloat(formData.electricityPrice),
          fuel_price: parseFloat(formData.fuelPrice),
          hot_water_setpoint: numberOrNull(formData.hotWaterSetpoint),
          water_heating_efficiency: numberOrNull(formData.waterHeatingEfficiency)
        }
      };

      /* =========================
        4️⃣ Récupération des risques
        ========================= */
      if (editingBuilding.risks) {
        buildingData.risks = editingBuilding.risks;
      } else {
        console.log("API risques lancée");
        const risksData = await fetchRisksData(formData.latitude, formData.longitude);
        buildingData.risks = risksData.risquesNaturels;
      }

      /* =========================
        5️⃣ Envoi complet à l'API
        ========================= */
      const response = await fetch(`${process.env.REACT_APP_API_URL}/update_building`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildingData),
      });

      if (response.ok) {
        toast.success("Bâtiment modifié avec succès !");
        fetchBuildings();
        clearForm();
      } else {
        toast.error("Erreur lors de la modification du bâtiment.");
      }

    } catch (error) {
      console.error("Erreur API:", error);
      toast.error("Erreur de connexion à l'API.");
    } finally {
      setIsSaving(false);
    }
  };




  const fetchRisksData = async (latitude, longitude) => {
    try {
      const endpoint = `https://georisques.gouv.fr/api/v1/resultats_rapport_risque?latlon=${longitude}%2C${latitude}`;
      const response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error("Erreur lors de la récupération des risques.");
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Erreur lors de la récupération des risques:", error);
      return {
        risquesNaturels: {
          'inondation': { 'present': "none", 'libelle': 'Inondation' },
          'risqueCotier': { 'present': "none", 'libelle': 'Risques côtiers (submersion marine, tsunami)' },
          'reculTraitCote': { 'present': "none", 'libelle': 'Recul du trait de cote' },
          'retraitGonflementArgile': { 'present': "none", 'libelle': 'Retrait gonflement des argiles' }
        }
      };
    }
  };

  const handleVerifyAddress = async () => {
    await getGpsCoordinates(formData.adress, formData.postalCode, formData.city);
    setAddressVerified(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDeleteBuilding = async (buildingId) => {
    Swal.fire({
      title: 'Attention',
      text: 'Êtes-vous sûr de vouloir supprimer ce bâtiment ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Non, annuler',
      customClass: 'swal-custom'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/delete_building/${buildingId}`, {
            method: 'DELETE',
          });

          if (response.ok) {
            toast.success("Bâtiment supprimé avec succès !");
            fetchBuildings();
          } else {
            toast.error("Erreur lors de la suppression.");
          }
        } catch (error) {
          console.log('Erreur API:', error);
          toast.error("Erreur de connexion à l'API.");
        }
      }
    });
  };

  const handleDuplicateBuilding = async (building) => {
    let clients = [];
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/get_clients`);
      if (response.ok) clients = await response.json();
    } catch (error) {
      console.log('Erreur API:', error);
    }
    if (clients.length === 0) {
      toast.error("Impossible de charger la liste des clients.");
      return;
    }

    const escapeHtml = (s) => String(s ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const clientOptions = clients
      .sort((a, b) => (a.name_client || '').localeCompare(b.name_client || ''))
      .map((c) => `<option value="${escapeHtml(c._id)}" ${c._id === id_client ? 'selected' : ''}>${escapeHtml(c.name_client)}</option>`)
      .join('');

    const { value, isConfirmed } = await Swal.fire({
      title: "Dupliquer l'audit",
      html: `
        <input id="swal-new-name" class="swal2-input" placeholder="Nom du nouveau bâtiment" value="${escapeHtml(building.name_building)} (copie)" />
        <select id="swal-target-client" class="swal2-select">${clientOptions}</select>
      `,
      showCancelButton: true,
      confirmButtonText: 'Dupliquer',
      cancelButtonText: 'Annuler',
      customClass: 'swal-custom',
      focusConfirm: false,
      preConfirm: () => {
        const newName = document.getElementById('swal-new-name').value.trim();
        const targetClientId = document.getElementById('swal-target-client').value;
        if (!newName) {
          Swal.showValidationMessage('Le nom du bâtiment est obligatoire');
          return false;
        }
        return { newName, targetClientId };
      },
    });
    if (!isConfirmed || !value) return;

    setIsSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/duplicate_building`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          building_id: building._id,
          target_client_id: value.targetClientId,
          new_building_name: value.newName,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Audit dupliqué (${data.equipments} équipements, ${data.usages} usages).`);
        if (value.targetClientId === id_client) fetchBuildings();
      } else {
        toast.error("Erreur lors de la duplication.");
      }
    } catch (error) {
      console.log('Erreur API:', error);
      toast.error("Erreur de connexion à l'API.");
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = () => {
    setFormData(defaultForm);
    setBaselineFormData(defaultForm);
    setIsEditing(false);
    setEditingBuildingId(null);
    setActiveEditButton(null);
    setVerifyButtonText("Vérifier l'adresse");
    setVerifyButtonClass("");
  };

  const handleEditBuilding = async (building) => {
    if (isDirty) {
      const result = await Swal.fire({
        title: 'Modifications non enregistrées',
        html:
          'Vous avez des modifications non enregistrées sur ce bâtiment.<br>' +
          'Voulez-vous continuer sans enregistrer ?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Continuer sans enregistrer',
        cancelButtonText: 'Rester sur la page',
        customClass: 'swal-custom'
      });
      if (!result.isConfirmed) return;
    }

    if (activeEditButton === building._id) {
      clearForm();
    } else {
      const newFormData = {
        buildingName: building.name_building || '',
        buildingSurface: building.surface || '',
        plotSurface: building.plot_surface || '',
        numberOfFloors : building.number_of_floors || '',
        personCount: building.person_count || '',
        buildingType: building.type_building || '',
        watershed: building.watershed || '',
        latitude: building.latitude || '',
        longitude: building.longitude || '',
        adress: building.adress || '',
        city: building.city || '',
        auditDate: building.audit?.audit_date || '',
        auditorName1: building.audit?.auditor_name_1 || '',
        auditorName2: building.audit?.auditor_name_2 || '',
        auditorMail1: building.audit?.auditor_mail_1 || '',
        auditorMail2: building.audit?.auditor_mail_2 || '',
        respondent: building.audit?.respondent || '',
        respondentMail: building.audit?.respondent_mail || '',
        postalCode: building.postal_code || '',
        buildingTypeRoof: building.roof?.building_type_roof || '',
        isGreenRoof: building.roof?.is_green_roof || false,
        surfaceRoof: building.roof?.surface_roof || '',
        cbs: building?.cbs || '',
        daysPerWeek: building.building_usage?.weekly_usage || '5',
        weeksPerYear: building.building_usage?.yearly_usage || '52',
        moreIntenseWeeks: building.building_usage?.more_intense_weeks || '0',
        lessIntenseWeeks: building.building_usage?.less_intense_weeks || '0',
        counterBrand: building.counter?.counter_brand || '',
        counterSize: building.counter?.counter_size || '',
        counterIndex: building.counter?.counter_index || '',
        waterSupplier: building.counter?.water_supplier || '',
        billingFrequency: building.counter?.billing_frequency || '',
        remoteReading: building.counter?.remote_reading || false,
        submeter: building.counter?.submeter || false,
        counterBrand: building.counter?.counter_brand || '',
        waterConsumptionYear1: building.global_water_data?.water_consumption_year_1 || '',
        waterConsumptionYear2: building.global_water_data?.water_consumption_year_2 || '',
        waterConsumptionYear3: building.global_water_data?.water_consumption_year_3 || '',
        waterConsumptionPrice1: building.global_water_data?.water_consumption_price_1 || '',
        waterConsumptionPrice2: building.global_water_data?.water_consumption_price_2 || '',
        waterConsumptionPrice3: building.global_water_data?.water_consumption_price_3 || '',
        referenceConsumption: building.global_water_data?.reference_consumption ?? '',
        referenceConsumptionComment: building.global_water_data?.reference_consumption_comment || '',
        waterHeatingType: building.global_water_data?.water_heating_type || '',
        waterPrice: building.global_water_data?.water_price || '',
        electricityPrice: building.global_water_data?.electricity_price || '',
        fuelPrice: building.global_water_data?.fuel_price || '',
        hotWaterSetpoint: building.global_water_data?.hot_water_setpoint ?? '',
        waterHeatingEfficiency: building.global_water_data?.water_heating_efficiency ?? '',
        building_users: building.building_users || [],
        functionalUnits: building.functional_units || []
      };
      setFormData(newFormData);
      setBaselineFormData(newFormData);
      setIsEditing(true);
      setEditingBuildingId(building._id);
      setActiveEditButton(building._id);
    }
  };

    useEffect(() => {
      if (id_client) {
      fetch(`${process.env.REACT_APP_API_URL}/get_client_data_by_id/${id_client}`)
          .then((response) => response.json())
          .then((data) => {
          setDataClient(data);
          })
          .catch((err) => setError(err.message));
      }
    }, [id_client]);

  return (
    <div className="audit-page">
      <ToastContainer />
      {isSaving && (
        <div className="saving-popup">
          <div className="saving-popup-content">
            <WalterLoader />
            <p>Enregistrement du bâtiment</p>
          </div>
        </div>
      )}
        <div className='building-table'>
            <h1>Liste des bâtiments - {dataClient?.name_client}</h1>
            <ul className="building-list">
                {buildings.length > 0 ? (
                    buildings.map((building) => (
                        <li key={building._id} className="building-item">
                            <span onClick={() => navigate(`${building._id}`)}>
                              <strong>{building.name_building}</strong>
                            </span>
                            <div className='edit-delete-icons'>
                              <FaEdit
                                className={`edit-icon ${activeEditButton === building._id ? 'active' : ''}`}
                                onClick={() => handleEditBuilding(building)}
                              />
                              <FaClone className="duplicate-icon" title="Dupliquer l'audit" onClick={() => handleDuplicateBuilding(building)} />
                              <FaTrash className="delete-icon" onClick={() => handleDeleteBuilding(building._id)} />
                            </div>
                        </li>
                    ))
                ) : (
                    <li className="no-buildings">Aucun bâtiment n'a été trouvé</li>
                )}
            </ul>
        </div>

        <h1 className="audit-page-add-building-title">Ajouter un nouveau bâtiment</h1>

        <div className="form-map-container">
            <div className="form-container">
                <h1>Informations générales</h1>
                <form>
                    <div className="form-group">
                      <input type="text" name="buildingName" placeholder='Nom du bâtiment' value={formData.buildingName} onChange={handleChange} required />
                    </div>
                    <div className="form-group-horizontal">
                      <label className="label-flex">Surface du bâtiment (m²)</label>
                      <input type="number" name="buildingSurface" value={formData.buildingSurface} onChange={handleChange} required />
                    </div>
                    <div className="form-group-horizontal">
                      <label className="label-flex">Surface de la parcelle (m²)</label>
                      <input type="number" name="plotSurface" value={formData.plotSurface} onChange={handleChange} required />
                    </div>
                    <div className="form-group-horizontal">
                      <label className="label-flex">Nombre d'étages</label>
                      <input type="number" name="numberOfFloors" value={formData.numberOfFloors} onChange={handleChange} required />
                    </div>
                    <div className="form-group-horizontal">
                      <label className="label-flex">Nombre de personnes</label>
                      <input type="number" name="personCount" value={formData.personCount} onChange={handleChange} required />
                    </div>
                    <div className="form-group-horizontal">
                      <label className="label-flex">Type de bâtiment</label>
                      <select name="buildingType" value={formData.buildingType} onChange={handleChange} required>
                        <option value="" disabled>Sélectionner un type</option>
                        {buildingTypes.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group-horizontal">
                      <label className="label-flex">Bassin versant</label>
                      <select name="watershed" value={formData.watershed} onChange={handleChange} required>
                        <option value="adour_garonne">Adour Garonne</option>
                        <option value="seine_normandie">Seine Normandie</option>
                        <option value="rhone_mediterranee">Rhône Méditerranée</option>
                        <option value="loire_bretage">Loire Bretagne</option>
                        <option value="rhin_meuse">Rhin Meuse</option>
                        <option value="artois_picardie">Artois Picardie</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Adresse</label>
                      <input type="text" name="adress" value={formData.adress} onChange={handleChange} required />
                    </div>
                    <div className="form-group-twice">
                      <div className="form-group">
                        <label>Code postal</label>
                        <input type="number" name="postalCode" value={formData.postalCode} onChange={handleChange} required />
                      </div>
                      <div className="form-group">
                        <label>Ville</label>
                        <input type="text" name="city" value={formData.city} onChange={handleChange} required />
                      </div>
                    </div>
                    <button type="button" className={`verify-button ${verifyButtonClass}`} onClick={handleVerifyAddress}>{verifyButtonText}</button>
                </form>
            </div>
            <div className="map-container">
                <MapContainer center={[46.603354, 1.888334]} zoom={5} className='map-in-container'>
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    {formData.latitude && formData.longitude && (
                        <Marker position={[formData.latitude, formData.longitude]} />
                    )}
                    <UpdateMapView coords={formData.latitude && formData.longitude ? [formData.latitude, formData.longitude] : null} />
                </MapContainer>
            </div>
        </div>
        <div className="additional-forms-container">
        <div className="water-form">
            <h2>Informations générales sur l'eau</h2>
            <form>
              <h3>Consommation et prix de l’eau</h3>
              <table className="water-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Année N-2</th>
                    <th>Année N-1</th>
                    <th>Année N</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Volume (m³)</td>
                    <td>
                      <input
                        type="number"
                        name="waterConsumptionYear1"
                        value={formData.waterConsumptionYear1}
                        onChange={handleChange}
                        min="0"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        name="waterConsumptionYear2"
                        value={formData.waterConsumptionYear2}
                        onChange={handleChange}
                        min="0"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        name="waterConsumptionYear3"
                        value={formData.waterConsumptionYear3}
                        onChange={handleChange}
                        min="0"
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>Prix (€)</td>
                    <td>
                      <input
                        type="number"
                        name="waterConsumptionPrice1"
                        value={formData.waterConsumptionPrice1}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        name="waterConsumptionPrice2"
                        value={formData.waterConsumptionPrice2}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        name="waterConsumptionPrice3"
                        value={formData.waterConsumptionPrice3}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              {/* Vide = année N. Sert de consommation réelle partout (dashboard, calage, rapport). */}
              <div className="form-group-inline">
                <label>Consommation de référence (m³/an)</label>
                <input type="number" name="referenceConsumption" value={formData.referenceConsumption} onChange={handleChange}
                  min="0" placeholder={formData.waterConsumptionYear3 ? `${formData.waterConsumptionYear3} (N)` : 'Année N'} style={{width:"100px"}}/>
              </div>
              <div className="form-group">
                <textarea name="referenceConsumptionComment" className="entity-notes-textarea" rows={2}
                  value={formData.referenceConsumptionComment} onChange={handleChange}
                  placeholder="Justification de la référence (ex. moyenne N-2/N-1 : fuite réparée en N)"/>
              </div>
              <div className="form-group-horizontal">
                <label className="label-flex">Type de chauffe-eau</label>
                <select name="waterHeatingType" value={formData.waterHeatingType} onChange={handleChange} required>
                  <option value="electricity">Electrique</option>
                  <option value="fuel">Gaz</option>
                  <option value="heat_pump">PAC</option>
                </select>
              </div>
              <div className="form-group-inline">
                <label>Prix de l'eau</label>
                <input type="number" name="waterPrice" value={formData.waterPrice} onChange={handleChange} style={{width:"100px"}}/>
              </div>
              <div className="form-group-inline">
                <label>Prix de l'électricité</label>
                <input type="number" name="electricityPrice" value={formData.electricityPrice} onChange={handleChange} style={{width:"100px"}}/>
              </div>
              <div className="form-group-inline">
                <label>Prix du gaz</label>
                <input type="number" name="fuelPrice" value={formData.fuelPrice} onChange={handleChange} style={{width:"100px"}}/>
              </div>
              {/* Vides = valeurs par défaut (metadata.json) : 55 °C, rendement selon le type de chauffe-eau */}
              <div className="form-group-inline">
                <label>Température du ballon (°C)</label>
                <input type="number" name="hotWaterSetpoint" value={formData.hotWaterSetpoint} onChange={handleChange}
                  min={HOT_WATER_PRODUCTION.cold_water_temperature + 1} max="90" step="1"
                  placeholder={String(HOT_WATER_PRODUCTION.default_setpoint)} style={{width:"100px"}}/>
              </div>
              <div className="form-group-inline">
                <label>{formData.waterHeatingType === 'heat_pump' ? 'COP de la PAC' : 'Rendement du chauffe-eau'}</label>
                <input type="number" name="waterHeatingEfficiency" value={formData.waterHeatingEfficiency} onChange={handleChange}
                  min="0.1" max="6" step="0.05"
                  placeholder={String(HOT_WATER_PRODUCTION.default_efficiency[formData.waterHeatingType] ?? 1)} style={{width:"100px"}}/>
              </div>
            </form>
        </div>
        <div className="audit-form">
              <h2>Informations sur l'audit</h2>
              <form>
                <div className="form-group-horizontal">
                  <label className="label-flex">Date de l'audit</label>
                  <input type="date" name="auditDate" value={formData.auditDate} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <input type="text" name="auditorName1" placeholder="Nom de l'auditeur 1" value={formData.auditorName1} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <input type="text" name="auditorMail1" placeholder="Mail de l'auditeur 1" value={formData.auditorMail1} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <input type="text" name="auditorName2" placeholder="Nom de l'auditeur 2" value={formData.auditorName2} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <input type="text" name="auditorMail2" placeholder="Mail de l'auditeur 2" value={formData.auditorMail2} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <input type="text" name="respondent" placeholder="Nom de la personne interrogée" value={formData.respondent} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <input type="text" name="respondentMail" placeholder="Mail de la personne interrogée" value={formData.respondentMail} onChange={handleChange} required />
                </div>
              </form>
          </div>
          <div className="counter-form">
              <h2>Informations sur le compteur</h2>
              <form>
                <div className="form-group-inline">
                  <label>Marque du compteur</label>
                  <input type="text" name="counterBrand" value={formData.counterBrand} onChange={handleChange} style={{width:"100px"}}/>
                </div>
                <div className="form-group-inline">
                  <label>Diamètre du compteur</label>
                  <input type="number" name="counterSize" value={formData.counterSize} onChange={handleChange} style={{width:"100px"}}/>
                </div>
                <div className="form-group-inline">
                  <label>Index du compteur</label>
                  <input type="number" name="counterIndex" value={formData.counterIndex} onChange={handleChange} style={{width:"100px"}}/>
                </div>
                <div className="form-group-inline">
                  <label>Fournisseur</label>
                  <input type="text" name="waterSupplier" value={formData.waterSupplier} onChange={handleChange} style={{width:"100px"}}/>
                </div>
                <div className="form-group">
                  <label>Fréquence de facturation</label>
                  <select name="billingFrequency" value={formData.billingFrequency} onChange={handleChange} required>
                          <option value="monthly">Mensuelle</option>
                          <option value="quarterly">Trimestrielle</option>
                          <option value="half_yearly">Semestrielle</option>
                          <option value="yearly">Annuelle</option>
                        </select>
                </div>
                <div className="form-group-inline">
                  <label>Télérelève ?</label>
                  <input type="checkbox" name="remoteReading" checked={formData.remoteReading} onChange={handleChange} />
                </div>
                <div className="form-group-inline">
                  <label>Sous-compteur ?</label>
                  <input type="checkbox" name="submeter" checked={formData.submeter} onChange={handleChange} />
                </div>
              </form>
          </div>
          <div className="toiture-form">
            <h2>Informations sur la toiture</h2>
            <form>
              <div className="form-group">
                <label>Type de toiture</label>
                <select name="buildingTypeRoof" value={formData.buildingTypeRoof} onChange={handleChange} required>
                        <option value="plate">Toiture plate</option>
                        <option value="pente">Toiture en pente</option>
                      </select>
              </div>
              <div className="form-group-inline">
                <label>Toiture végétalisée ?</label>
                <input type="checkbox" name="isGreenRoof" checked={formData.isGreenRoof} onChange={handleChange} />
              </div>
              <div className="form-group-inline">
                <label>Surface de toiture</label>
                <input type="number" name="surfaceRoof" value={formData.surfaceRoof} onChange={handleChange} style={{width:"100px"}}/> m2
              </div>
            </form>
          </div>
          <div className="surfaces-form">
            <h2>Informations sur les surfaces (CBS)</h2>
            <CbsPanel
              buildingId={editingBuildingId}
              latitude={formData.latitude}
              longitude={formData.longitude}
              detail={buildings.find((b) => b._id === editingBuildingId)?.cbs_detail}
              plotSurface={formData.plotSurface}
              onComputed={handleCbsComputed}
              onUsePlotSurface={(value) => setFormData((prev) => ({ ...prev, plotSurface: value }))}
            />
            <form>
              <div className="form-group-inline">
                <label>CBS retenu (modifiable si le calcul est impossible)</label>
                <input type="number" name="cbs" value={formData.cbs} onChange={handleChange} style={{width:"100px"}}/>
              </div>
            </form>
          </div>
          <div className="presence-form">
            <h2>Présence dans le bâtiment</h2>
            <form>
              <div className="form-group-inline">
                <label>Jours par semaine</label>
                <input type="number" name="daysPerWeek" onChange={handleChange} value={formData.daysPerWeek} style={{width:"100px"}}/>
              </div>
              <div className="form-group-inline">
                <label>Semaines par an</label>
                <input type="number" name="weeksPerYear" onChange={handleChange} value={formData.weeksPerYear} style={{width:"100px"}}/>
              </div>
              <div className="form-group-inline">
                <label>Semaines plus intenses</label>
                <input type="number" name="moreIntenseWeeks" onChange={handleChange} value={formData.moreIntenseWeeks} style={{width:"100px"}}/>
              </div>
              <div className="form-group-inline">
                <label>Semaines moins intenses</label>
                <input type="number" name="lessIntenseWeeks" onChange={handleChange} value={formData.lessIntenseWeeks} style={{width:"100px"}}/>
              </div>
            </form>
          </div>

          <div className="building-users-form">
            <BuildingUsersReadOnly users={formData.building_users || []} buildingId={editingBuildingId} />
          </div>
          <FunctionalUnitsForm formData={formData} setFormData={setFormData} setBaselineFormData={setBaselineFormData} buildingId={editingBuildingId} />
        </div>

        
        <div className="save-button-building">
          <button
            type="submit"
            onClick={isEditing ? handleSubmit : handleCreate} // ← ici !
            disabled={!coordinatesFound && !isEditing} // toujours bloqué si pas de coordonnées pour la création
          >
            {isEditing ? 'Modifier le bâtiment' : 'Valider'}
          </button>
        </div>

    </div>
  );
}

export default AuditPageCreationBuilding;